// Auto-create activity log — ONE dialog shown from the recon dashboard AND the recurring report. A simple audit of
// what the auto-create cron (every PSP) has created/skipped, with basic details. Server-side filtered + paginated
// (recent first) so it stays fast as the log grows. Filters: date range, PSP, and a donor / product / order# search.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Box, Typography, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, CircularProgress, Alert, TextField, FormControl,
  Select, MenuItem, Pagination, InputAdornment, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { get } from '../../utils/api';
import { fmtMoney } from './reconShared';

const PROVIDER_LABEL = { authnet: 'Authorize.Net', paysimple: 'PaySimple' };
const PROVIDER_COLOR = { authnet: 'info', paysimple: 'secondary' };
const ACTION_OPTS = [
  { value: 'all', label: 'All actions' }, { value: 'created', label: 'Created' },
  { value: 'skipped_duplicate', label: 'Skipped — duplicate' }, { value: 'skipped_multi_product', label: 'Skipped — multi-product' },
  { value: 'skipped_other', label: 'Skipped — other' }, { value: 'failed', label: 'Failed' },
];
const actionColor = (a) => (a === 'created' ? 'success' : a === 'failed' ? 'error' : a === 'skipped_duplicate' ? 'warning' : 'default');
const actionLabel = (a) => ({
  created: 'Created', skipped_duplicate: 'Duplicate', skipped_multi_product: 'Multi-product',
  skipped_other: 'Skipped', skipped_no_subscription: 'No subscription', skipped_unmapped: 'Unmapped', failed: 'Failed',
}[a] || a);
const fmtDT = (d) => (d ? new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

export default function AutocreateLogDialog({ open, onClose }) {
  const [provider, setProvider] = useState('all');
  const [action, setAction] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [qLive, setQLive] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { const t = setTimeout(() => setQ(qLive.trim()), 350); return () => clearTimeout(t); }, [qLive]);
  useEffect(() => { setPage(1); }, [provider, action, from, to, q]);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true); setErr('');
    try {
      setData(await get('/api/manage/autocreate-log', {
        provider, action, from: from || undefined, to: to || undefined, q: q || undefined, page, per_page: 25,
      }));
    } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to load the log'); }
    finally { setLoading(false); }
  }, [open, provider, action, from, to, q, page]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const summary = data?.summary || [];
  const count = (a) => Number((summary.find((s) => s.action === a) || {}).n || 0);

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Auto-create activity log
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 400 }}>
          What the auto-create cron has created / skipped{data?.last_run ? ` · last run ${fmtDT(data.last_run)}` : ''}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
          <TextField size="small" placeholder="Search donor / product / order #" value={qLive} onChange={(e) => setQLive(e.target.value)}
            sx={{ minWidth: 260, flex: 1 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <FormControl size="small"><Select value={provider} onChange={(e) => setProvider(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="all">All PSPs</MenuItem><MenuItem value="authnet">Authorize.Net</MenuItem><MenuItem value="paysimple">PaySimple</MenuItem>
          </Select></FormControl>
          <FormControl size="small"><Select value={action} onChange={(e) => setAction(e.target.value)} sx={{ minWidth: 170 }}>
            {ACTION_OPTS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select></FormControl>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} sx={{ width: 150 }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} sx={{ width: 150 }} />
          <Tooltip title="Refresh"><IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
        </Box>
        {/* Summary chips for the current filter */}
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
          <Chip size="small" color="success" variant="outlined" label={`Created ${count('created')}`} />
          <Chip size="small" color="warning" variant="outlined" label={`Duplicate ${count('skipped_duplicate')}`} />
          <Chip size="small" variant="outlined" label={`Multi-product ${count('skipped_multi_product')}`} />
          <Chip size="small" color="error" variant="outlined" label={`Failed ${count('failed')}`} />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center' }}>{data?.total ?? 0} total</Typography>
        </Box>

        {err && <Alert severity="error" sx={{ mb: 1.5 }}>{err}</Alert>}
        {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>
          : (
            <Table size="small" sx={{ '& td, & th': { px: 1.25 }, '& thead th': { bgcolor: 'grey.50', fontWeight: 700 } }}>
              <TableHead><TableRow>
                <TableCell>When</TableCell><TableCell>PSP</TableCell><TableCell>Donor</TableCell>
                <TableCell>Product / variation</TableCell><TableCell align="right">Amount</TableCell>
                <TableCell>Action</TableCell><TableCell>Order</TableCell><TableCell>Details</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}>No log entries match these filters.</TableCell></TableRow>}
                {rows.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDT(r.run_at)}</TableCell>
                    <TableCell><Chip size="small" variant="outlined" color={PROVIDER_COLOR[r.provider] || 'default'} label={PROVIDER_LABEL[r.provider] || r.provider} /></TableCell>
                    <TableCell>{r.donor_name || '—'}</TableCell>
                    <TableCell>{r.product_label || '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{r.amount_num != null ? fmtMoney(r.amount_num) : '—'}</TableCell>
                    <TableCell><Chip size="small" color={actionColor(r.action)} variant={r.action === 'created' ? 'filled' : 'outlined'} label={actionLabel(r.action)} /></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{r.order_number ? `#${r.order_number}` : (r.existing_order || '—')}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{[r.ref, r.reason].filter(Boolean).join(' · ') || '—'}{r.created_by ? <Box component="span" sx={{ display: 'block', opacity: 0.8 }}>by {r.created_by}</Box> : null}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Pagination count={data?.pages || 1} page={page} onChange={(e, p) => setPage(p)} size="small" color="primary" siblingCount={0} boundaryCount={1} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
